import React, { useState } from "react";
import {
    updateHostUrls,
    copyPrompts,
    clearCache,
    getClearCache,
    mergeCategories,
    testInformationAndResultPageFix,
    updateTestMarks
} from "../../api/Troubleshoot";
import Modal from "react-bootstrap/Modal";
import Snackbar from "react-js-snackbar";
import { Card, Container, Row, Col, Form, Button, Spinner } from "react-bootstrap";

const Troubleshoot = () => {
    const [sourceSubCategoryId, setSourceSubCategoryId] = useState("");
    const [targetSubCategoryId, setTargetSubCategoryId] = useState("");
    const [posMarks, setPosMarks] = useState(1);
    const [negMarks, setNegMarks] = useState(0);

    // States for modals
    const [showModal, setShowModal] = useState(false);
    const [modalAction, setModalAction] = useState("");
    const [modalText, setModalText] = useState("");

    // States for loading
    const [isLoading, setIsLoading] = useState({
        reloadImage: false,
        showSolutions: false,
        clearCache: false,
        fixTest: false,
        mergeCategories: false,
        updateMarks: false
    });

    // States for success indicators
    const [successState, setSuccessState] = useState({
        reloadImage: false,
        showSolutions: false,
        clearCache: false,
        fixTest: false,
        mergeCategories: false,
        updateMarks: false
    });

    // States for snackbar
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarColor, setSnackbarColor] = useState("");

    // Handle confirmation dialog open
    const openConfirmationModal = (action, text) => {
        setModalAction(action);
        setModalText(text);
        setShowModal(true);
    };

    // Handle confirmation dialog close
    const closeConfirmationModal = () => {
        setShowModal(false);
    };

    // Execute confirmed action
    const executeConfirmedAction = () => {
        closeConfirmationModal();

        switch (modalAction) {
            case "reloadImage":
                handleLayer1ApiCall();
                break;
            case "showSolutions":
                handleLayer2ApiCall();
                break;
            case "clearCache":
                handleLayer3ApiCall();
                break;
            case "fixTest":
                handleLayer5ApiCall();
                break;
            case "mergeCategories":
                handleLayer4ApiCall();
                break;
            case "updateMarks":
                handleLayer6ApiCall();
                break;
            default:
                break;
        }
    };

    // Reset success indicator after delay
    const resetSuccessAfterDelay = (key) => {
        setTimeout(() => {
            setSuccessState(prev => ({ ...prev, [key]: false }));
        }, 3000);
    };

    // Show notification
    const showNotification = (message, color = "success") => {
        setSnackbarMessage(message);
        setSnackbarColor(color);
        setShowSnackbar(true);

        // Hide notification after 3 seconds
        setTimeout(() => {
            setShowSnackbar(false);
        }, 3000);
    };

    const handleLayer1ApiCall = async () => {
        try {
            setIsLoading({ ...isLoading, reloadImage: true });
            const data = await updateHostUrls();
            console.log("Layer 1 API Response:", data);
            showNotification("Images reloaded successfully!");
            setSuccessState({ ...successState, reloadImage: true });
            resetSuccessAfterDelay("reloadImage");
        } catch (error) {
            console.error("Error in Layer 1 API call:", error);
            showNotification("Failed to reload images", "error");
        } finally {
            setIsLoading({ ...isLoading, reloadImage: false });
        }
    };

    const handleLayer2ApiCall = async () => {
        try {
            setIsLoading({ ...isLoading, showSolutions: true });
            const data = await copyPrompts();
            console.log("Layer 2 API Response:", data);
            showNotification("Solutions are now visible!");
            setSuccessState({ ...successState, showSolutions: true });
            resetSuccessAfterDelay("showSolutions");
        } catch (error) {
            console.error("Error in Layer 2 API call:", error);
            showNotification("Failed to show solutions", "error");
        } finally {
            setIsLoading({ ...isLoading, showSolutions: false });
        }
    };

    const handleLayer3ApiCall = async () => {
        try {
            setIsLoading({ ...isLoading, clearCache: true });
            // First call the POST endpoint
            const postData = await clearCache();
            console.log("Layer 3 API Response (POST):", postData);

            // Then call the GET endpoint
            const getData = await getClearCache();
            console.log("Layer 3 API Response (GET):", getData);

            showNotification("Cache cleared successfully!");
            setSuccessState({ ...successState, clearCache: true });
            resetSuccessAfterDelay("clearCache");
        } catch (error) {
            console.error("Error in Layer 3 API call:", error);
            showNotification("Failed to clear cache", "error");
        } finally {
            setIsLoading({ ...isLoading, clearCache: false });
        }
    };

    const handleLayer5ApiCall = async () => {
        try {
            setIsLoading({ ...isLoading, fixTest: true });
            const data = await testInformationAndResultPageFix();
            console.log("Layer 5 API Response:", data);
            showNotification("Test Information and Result Page fixed!");
            setSuccessState({ ...successState, fixTest: true });
            resetSuccessAfterDelay("fixTest");
        } catch (error) {
            console.error("Error in Layer 5 API call:", error);
            showNotification("Failed to fix Test Information and Result Page", "error");
        } finally {
            setIsLoading({ ...isLoading, fixTest: false });
        }
    };

    const handleLayer4ApiCall = async () => {
        try {
            setIsLoading({ ...isLoading, mergeCategories: true });
            const data = await mergeCategories(sourceSubCategoryId, targetSubCategoryId);
            console.log("Layer 4 API Response:", data);
            showNotification("Categories merged successfully!");
            setSuccessState({ ...successState, mergeCategories: true });
            resetSuccessAfterDelay("mergeCategories");
            // Clear input fields after successful merge
            setSourceSubCategoryId("");
            setTargetSubCategoryId("");
        } catch (error) {
            console.error("Error in Layer 4 API call:", error);
            showNotification("Failed to merge categories", "error");
        } finally {
            setIsLoading({ ...isLoading, mergeCategories: false });
        }
    };

    const handleLayer6ApiCall = async () => {
        try {
            setIsLoading({ ...isLoading, updateMarks: true });
            const data = await updateTestMarks(posMarks, negMarks);
            console.log("Layer 6 API Response:", data);
            showNotification("Test marks updated successfully!");
            setSuccessState({ ...successState, updateMarks: true });
            resetSuccessAfterDelay("updateMarks");
        } catch (error) {
            console.error("Error in Layer 6 API call:", error);
            showNotification("Failed to update test marks", "error");
        } finally {
            setIsLoading({ ...isLoading, updateMarks: false });
        }
    };

    // Function to render button with loading and success states
    const renderActionButton = (
        label,
        action,
        confirmText,
        loadingKey,
        successKey,
        icon = "🔄",
        successIcon = "✅",
        variant = "primary"
    ) => {
        return (
            <Card className="action-card mb-3">
                <Card.Body>
                    <Row className="align-items-center">
                        <Col xs={1} className="text-center">
                            <div className="icon-container">
                                {icon}
                            </div>
                        </Col>
                        <Col>
                            <h5 className="mb-0">{label}</h5>
                        </Col>
                        <Col xs={3} className="text-end">
                            <Button
                                variant={successState[successKey] ? "success" : variant}
                                onClick={() => openConfirmationModal(action, confirmText)}
                                disabled={isLoading[loadingKey]}
                                className="action-button"
                            >
                                {isLoading[loadingKey] ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        Loading...
                                    </>
                                ) : successState[successKey] ? (
                                    <>{successIcon} Done</>
                                ) : (
                                    "Execute"
                                )}
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        );
    };

    return (
        <Container fluid className="py-4 px-4">
            <Card className="mb-4 shadow-sm">
                <Card.Header className="bg-primary text-white">
                    <h3 className="mb-0">Troubleshoot Panel</h3>
                    <p className="mb-0 text-white-50">Tools for fixing common issues</p>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <div className="troubleshoot-section pb-3">
                                <h4 className="border-bottom pb-2 mb-3">Image & Content Issues</h4>

                                {renderActionButton(
                                    "Fix Images Not Loading in Tests",
                                    "reloadImage",
                                    "Are you sure you want to reload images?",
                                    "reloadImage",
                                    "reloadImage",
                                    "🖼️"
                                )}

                                {renderActionButton(
                                    "Make Solutions Visible in Tests",
                                    "showSolutions",
                                    "Are you sure you want to show solutions?",
                                    "showSolutions",
                                    "showSolutions",
                                    "📝"
                                )}
                            </div>
                        </Col>

                        <Col md={6}>
                            <div className="troubleshoot-section pb-3">
                                <h4 className="border-bottom pb-2 mb-3">System Maintenance</h4>

                                {renderActionButton(
                                    "Clear System Cache",
                                    "clearCache",
                                    "Are you sure you want to clear the cache?",
                                    "clearCache",
                                    "clearCache",
                                    "🧹",
                                    "✅",
                                    "warning"
                                )}

                                {renderActionButton(
                                    "Fix Test Information and Result Pages",
                                    "fixTest",
                                    "Are you sure you want to fix the Test Information and Result Pages?",
                                    "fixTest",
                                    "fixTest",
                                    "🛠️"
                                )}
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="shadow-sm">
                <Card.Header className="bg-secondary text-white">
                    <h4 className="mb-0">Advanced Tools</h4>
                </Card.Header>
                <Card.Body>
                    <div className="troubleshoot-section">
                        <Card className="mb-3">
                            <Card.Body>
                                <Row className="align-items-center">
                                    <Col xs={1} className="text-center">
                                        <div className="icon-container">
                                            🔄
                                        </div>
                                    </Col>
                                    <Col>
                                        <h5 className="mb-3">Merge Categories</h5>
                                        <Form.Group className="mb-3">
                                            <Row>
                                                <Col md={5}>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Source SubCategory ID"
                                                        value={sourceSubCategoryId}
                                                        onChange={(e) => setSourceSubCategoryId(e.target.value)}
                                                    />
                                                </Col>
                                                <Col md={5}>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Target SubCategory ID"
                                                        value={targetSubCategoryId}
                                                        onChange={(e) => setTargetSubCategoryId(e.target.value)}
                                                    />
                                                </Col>
                                                <Col md={2}>
                                                    <Button
                                                        variant={successState.mergeCategories ? "success" : "danger"}
                                                        onClick={() => openConfirmationModal("mergeCategories", "Are you sure you want to merge these categories? This action cannot be undone.")}
                                                        disabled={sourceSubCategoryId.trim() === "" || targetSubCategoryId.trim() === "" || isLoading.mergeCategories}
                                                        className="w-100"
                                                    >
                                                        {isLoading.mergeCategories ? (
                                                            <>
                                                                <Spinner
                                                                    as="span"
                                                                    animation="border"
                                                                    size="sm"
                                                                    role="status"
                                                                    aria-hidden="true"
                                                                    className="me-2"
                                                                />
                                                                Merging...
                                                            </>
                                                        ) : successState.mergeCategories ? (
                                                            <>✅ Done</>
                                                        ) : (
                                                            "Merge"
                                                        )}
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </Form.Group>
                                        <small className="text-muted">
                                            Warning: Merging categories cannot be undone. Ensure you have the correct IDs before proceeding.
                                        </small>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        <Card className="mb-0">
                            <Card.Body>
                                <Row className="align-items-center">
                                    <Col xs={1} className="text-center">
                                        <div className="icon-container">
                                            ⚙️
                                        </div>
                                    </Col>
                                    <Col>
                                        <h5 className="mb-3">Update Test Marks</h5>
                                        <Form.Group className="mb-3">
                                            <Row>
                                                <Col md={5}>
                                                    <Form.Label>Positive Marks</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        placeholder="Positive Marks"
                                                        value={posMarks}
                                                        onChange={(e) => setPosMarks(parseFloat(e.target.value))}
                                                        min="0"
                                                        step="0.5"
                                                    />
                                                </Col>
                                                <Col md={5}>
                                                    <Form.Label>Negative Marks</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        placeholder="Negative Marks"
                                                        value={negMarks}
                                                        onChange={(e) => setNegMarks(parseFloat(e.target.value))}
                                                        min="0"
                                                        step="0.25"
                                                    />
                                                </Col>
                                                <Col md={2} className="d-flex align-items-end">
                                                    <Button
                                                        variant={successState.updateMarks ? "success" : "info"}
                                                        onClick={() => openConfirmationModal("updateMarks", "Are you sure you want to update test marks? This will affect test ID 1.")}
                                                        disabled={isLoading.updateMarks}
                                                        className="w-100 mb-1"
                                                    >
                                                        {isLoading.updateMarks ? (
                                                            <>
                                                                <Spinner
                                                                    as="span"
                                                                    animation="border"
                                                                    size="sm"
                                                                    role="status"
                                                                    aria-hidden="true"
                                                                    className="me-2"
                                                                />
                                                                Updating...
                                                            </>
                                                        ) : successState.updateMarks ? (
                                                            <>✅ Done</>
                                                        ) : (
                                                            "Update"
                                                        )}
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </Form.Group>
                                        <small className="text-muted">
                                            This will update marks for test ID 1. Enter positive and negative marks values carefully.
                                        </small>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </div>
                </Card.Body>
            </Card>

            {/* Confirmation Modal */}
            <Modal show={showModal} onHide={closeConfirmationModal} centered>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title>Confirm Action</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center py-3">
                        <div className="mb-3 fs-1">⚠️</div>
                        <h5>{modalText}</h5>
                        <p className="text-muted mb-0">
                            This action will execute immediately once confirmed.
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeConfirmationModal}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={executeConfirmedAction}>
                        Confirm
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Snackbar for notifications */}
            <Snackbar
                show={showSnackbar}
                message={snackbarMessage}
                backgroundColor={snackbarColor === "error" ? "#d9534f" : "#5cb85c"}
            />

            <style jsx>{`
                .icon-container {
                    font-size: 1.5rem;
                }
                .action-card {
                    transition: all 0.3s ease;
                    border-left: 4px solid #007bff;
                }
                .action-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .action-button {
                    min-width: 100px;
                }
                .troubleshoot-section {
                    position: relative;
                }
                .card {
                    overflow: hidden;
                }
            `}</style>
        </Container>
    );
};

export default Troubleshoot;